import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography } from '@mui/material';

export interface AppIconProps {
  id: string;
  name: string;
  description?: string;
  icon: React.ReactNode;
  gradient?: string;
  isActive?: boolean;
  isFolder?: boolean;
  itemCount?: number;
  onClick: () => void;
}

const MotionBox = motion(Box);

const getGradientCss = (grad?: string): string => {
  if (!grad) return 'linear-gradient(135deg, #10b981, #0f766e)';
  if (grad.includes('linear-gradient')) return grad;
  if (grad.includes('blue') && grad.includes('indigo')) return 'linear-gradient(135deg, #2563eb, #4338ca)';
  if (grad.includes('purple') && grad.includes('rose')) return 'linear-gradient(135deg, #9333ea, #db2777, #e11d48)';
  if (grad.includes('purple') && grad.includes('indigo')) return 'linear-gradient(135deg, #a855f7, #4f46e5)';
  if (grad.includes('fuchsia')) return 'linear-gradient(135deg, #d946ef, #db2777)';
  if (grad.includes('pink') && grad.includes('rose')) return 'linear-gradient(135deg, #ec4899, #e11d48)';
  if (grad.includes('rose') && grad.includes('orange')) return 'linear-gradient(135deg, #f43f5e, #f97316)';
  if (grad.includes('violet') && grad.includes('purple')) return 'linear-gradient(135deg, #7c3aed, #6b21a8)';
  if (grad.includes('amber') && grad.includes('orange')) return 'linear-gradient(135deg, #f59e0b, #ea580c)';
  if (grad.includes('cyan') && grad.includes('blue')) return 'linear-gradient(135deg, #06b6d4, #2563eb)';
  if (grad.includes('teal') && grad.includes('emerald')) return 'linear-gradient(135deg, #0d9488, #047857)';
  if (grad.includes('slate') && grad.includes('zinc')) return 'linear-gradient(135deg, #334155, #18181b)';
  if (grad.includes('teal') && grad.includes('green')) return 'linear-gradient(135deg, #0d9488, #059669, #15803d)';
  if (grad.includes('slate')) return 'linear-gradient(135deg, #334155, #0f172a)';
  return 'linear-gradient(135deg, #10b981, #0f766e)';
};

export const AppIcon: React.FC<AppIconProps> = ({
  id,
  name,
  description,
  icon,
  gradient = 'from-emerald-500 to-teal-600',
  isActive = false,
  isFolder = false,
  itemCount,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const renderedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<{ isHovered?: boolean }>, {
        isHovered,
      })
    : icon;

  return (
    <MotionBox
      component="button"
      layoutId={`app-tile-${id}`}
      whileHover={{ scale: 1.06, y: -2 }}
      whileTap={{ scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30, mass: 1 }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        p: 1.5,
        borderRadius: 4,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background-color 0.2s',
        '&:hover': {
          bgcolor: 'rgba(241, 245, 249, 0.7)',
          '& .app-icon-box': {
            boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.25)',
          },
        },
        '&:focus': {
          outline: 'none',
        },
      }}
    >
      {/* Icon Card Container */}
      <MotionBox
        layoutId={`app-icon-bg-${id}`}
        className="app-icon-box"
        sx={{
          position: 'relative',
          width: { xs: 60, sm: 64 },
          height: { xs: 60, sm: 64 },
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: getGradientCss(gradient),
          color: '#ffffff',
          transition: 'all 0.3s ease',
          boxShadow: isActive
            ? '0 0 0 3px rgba(16, 185, 129, 0.4), 0 8px 16px -4px rgba(0, 0, 0, 0.15)'
            : '0 4px 12px -2px rgba(0, 0, 0, 0.12)',
        }}
      >
        {/* Subtle glass reflection overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 4,
            background:
              'linear-gradient(to bottom, rgba(255,255,255,0.3), transparent, rgba(0,0,0,0.05))',
            pointerEvents: 'none',
          }}
        />

        {/* Icon */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& > div': { display: 'flex', alignItems: 'center', justifyContent: 'center' },
            '& > img, & > svg, & > div > svg': { width: 28, height: 28 },
          }}
        >
          {renderedIcon}
        </Box>

        {/* Active Dot Badge */}
        {isActive && (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: 14,
              height: 14,
              bgcolor: '#10b981',
              border: '2px solid #ffffff',
              borderRadius: '50%',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}
          />
        )}

        {/* Folder badge with count */}
        {isFolder && itemCount && itemCount > 0 && (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              fontSize: '10px',
              fontWeight: 700,
              px: 0.75,
              py: 0.25,
              bgcolor: '#ffffff',
              color: '#047857',
              border: '1px solid #a7f3d0',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              borderRadius: 10,
            }}
          >
            {itemCount}
          </Box>
        )}
      </MotionBox>

      {/* Label */}
      <Typography
        variant="caption"
        noWrap
        sx={{
          mt: 1,
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '-0.025em',
          maxWidth: 90,
          display: 'block',
          transition: 'color 0.2s',
          color: isActive ? '#047857' : '#334155',
        }}
      >
        {name}
      </Typography>

      {description && (
        <Typography
          variant="caption"
          noWrap
          sx={{
            fontSize: '10px',
            color: '#94a3b8',
            maxWidth: 100,
            display: 'block',
            mt: 0.25,
            transition: 'color 0.2s',
          }}
        >
          {description}
        </Typography>
      )}
    </MotionBox>
  );
};

export default AppIcon;
